import React, { useState } from 'react';
import { Crown, Lightning, CheckCircle, X, Star, Lock } from '@phosphor-icons/react';
import api from '../services/api';
import { toast } from 'sonner';

export const UpgradeModal = ({ isOpen, onClose, onUpgraded }) => {
  const [upgrading, setUpgrading] = useState(null);

  const handleUpgrade = async (plan) => {
    setUpgrading(plan);
    try {
      // Create Stripe checkout session
      const response = await api.post('/stripe/create-checkout-session', null, {
        params: { plan }
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
      
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout');
      setUpgrading(null);
    }
  };

  if (!isOpen) return null;

  const plans = [
    {
      id: 'free',
      name: 'Free',
      icon: Lock,
      color: 'text-white/50',
      bgColor: 'bg-white/5',
      borderColor: 'border-white/10',
      features: [
        { text: '1 SharpSlip every 3 days', available: true },
        { text: 'Basic match analysis', available: true },
        { text: 'Limited access', available: true },
        { text: 'Unlimited SharpSlips', available: false },
        { text: 'Advanced insights', available: false }
      ],
      badge: 'Current Plan'
    },
    {
      id: 'normal',
      name: 'Normal',
      icon: Star,
      color: 'text-secondary',
      bgColor: 'bg-gradient-to-br from-secondary/20 to-secondary/10',
      borderColor: 'border-secondary',
      recommended: true,
      features: [
        { text: 'Unlimited SharpSlips', available: true },
        { text: 'Full match analysis', available: true },
        { text: 'Confidence scores', available: true },
        { text: 'All matches access', available: true },
        { text: 'Advanced insights', available: false }
      ]
    },
    {
      id: 'vip',
      name: 'VIP',
      icon: Crown,
      color: 'text-primary',
      bgColor: 'bg-gradient-to-br from-primary/20 to-primary/10',
      borderColor: 'border-primary',
      features: [
        { text: 'Everything in Normal', available: true },
        { text: 'Advanced AI insights', available: true },
        { text: 'Value bets detection', available: true },
        { text: 'Premium features', available: true },
        { text: 'Priority support', available: true }
      ]
    }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <div className="relative bg-accent border border-white/10 rounded-sm max-w-6xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors z-10"
        >
          <X weight="bold" className="text-2xl" />
        </button>

        <div className="p-8 md:p-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-heading font-black text-white mb-2">
              Unlock Premium Features
            </h2>
            <p className="text-white/60 text-lg">Choose the plan that fits your betting strategy</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            {plans.map((plan) => {
              const Icon = plan.icon;
              return (
                <div
                  key={plan.id}
                  className={`${plan.bgColor} border-2 ${plan.borderColor} rounded-sm p-6 relative ${
                    plan.recommended ? 'scale-105' : ''
                  }`}
                >
                  {plan.recommended && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-secondary text-background px-3 py-1 text-xs font-bold uppercase rounded-full">
                      Recommended
                    </div>
                  )}
                  {plan.badge && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white/10 text-white/50 px-3 py-1 text-xs font-bold uppercase rounded-full">
                      {plan.badge}
                    </div>
                  )}
                  
                  <div className="flex items-center gap-3 mb-4">
                    <Icon weight="fill" className={`${plan.color} text-2xl`} />
                    <span className={`text-xl font-heading font-bold ${plan.color}`}>{plan.name}</span>
                  </div>
                  
                  <div className="space-y-2.5 mb-6">
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2.5">
                        {feature.available ? (
                          <CheckCircle weight="fill" className={`${plan.color} text-base mt-0.5 flex-shrink-0`} />
                        ) : (
                          <X weight="bold" className="text-white/20 text-base mt-0.5 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${feature.available ? 'text-white/80' : 'text-white/30 line-through'}`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>

                  {plan.id !== 'free' && (
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={upgrading === plan.id}
                      className={`w-full px-4 py-3 ${
                        plan.id === 'normal' 
                          ? 'bg-secondary hover:bg-secondary/90' 
                          : 'bg-primary hover:bg-primary/90'
                      } text-background font-bold rounded-full transition-all duration-150 disabled:opacity-50 flex items-center justify-center gap-2`}
                    >
                      {upgrading === plan.id ? (
                        <>
                          <div className="w-4 h-4 border-2 border-background border-t-transparent rounded-full animate-spin" />
                          Upgrading...
                        </>
                      ) : (
                        <>
                          <Lightning weight="fill" />
                          Upgrade to {plan.name}
                        </>
                      )}
                    </button>
                  )}
                  
                  {plan.id !== 'free' && (
                    <div className="text-center mt-3">
                      <div className="text-white font-mono font-bold text-lg mb-1">
                        {plan.id === 'normal' ? '€9.99' : '€19.99'}
                        <span className="text-white/40 text-sm ml-1">/month</span>
                      </div>
                      <div className="text-xs text-white/40">Secure payment via Stripe</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="text-center text-white/50 text-sm">
            Secure payment processing powered by Stripe. Cancel anytime.
          </div>
        </div>
      </div>
    </div>
  );
};
